import { useState, useCallback } from 'react';
import api from '../api/axios';

/**
 * Custom hook for managing multi-phase flow builder
 * Handles planning, building batches, and checkpoints
 * 
 * NEW: Automatic mode detection via analyzeRequest
 * NEW: Feature gap detection
 */
const useMultiPhaseFlowBuilder = (sessionId) => {
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [checkpoint, setCheckpoint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(null); // 'analyzing', 'planning', 'planned', 'building', 'completed'
  const [analysisResult, setAnalysisResult] = useState(null);
  const [featureGaps, setFeatureGaps] = useState([]);

  /**
   * NEW: Intelligent request analysis
   * Automatically determines mode and detects feature gaps
   */
  const analyzeRequest = useCallback(async (message) => {
    setLoading(true);
    setError(null);
    setPhase('analyzing');

    try {
      const response = await api.post('/ai-flow-builder/analyze', {
        sessionId,
        message
      });

      const result = response.data;
      setAnalysisResult(result);

      // Handle feature gaps
      if (result.hasFeatureGaps) {
        setFeatureGaps(result.featureGaps);
        setPhase('feature_gap');
        return result;
      }

      // Handle multi-phase mode - plan is already created
      if (result.mode === 'multi-phase' && result.plan) {
        setPlan(result.plan);
        setPlanId(result.planId);
        setPhase('planned');
        return result;
      }

      // Standard mode
      setPhase(null);
      return result;
    } catch (err) {
      console.error('Error analyzing request:', err);
      setError(err.response?.data?.message || 'Failed to analyze request');
      setPhase(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  /**
   * Phase 1: Create a plan from user requirements
   * (Legacy method - analyzeRequest is preferred)
   */
  const createPlan = useCallback(async (requirements) => {
    setLoading(true);
    setError(null);
    setPhase('planning');

    try {
      const response = await api.post('/ai-flow-builder/multi-phase/plan', {
        sessionId,
        requirements
      });

      setPlan(response.data.plan);
      setPlanId(response.data.planId);
      setPhase('planned');

      return response.data;
    } catch (err) {
      console.error('Error creating plan:', err);
      setError(err.response?.data?.message || 'Failed to create plan');
      setPhase(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  /**
   * Modify an existing plan
   */
  const modifyPlan = useCallback(async (modifications) => {
    if (!planId) {
      throw new Error('No plan ID available');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/ai-flow-builder/multi-phase/modify-plan', {
        planId,
        modifications
      });

      setPlan(response.data.plan);
      
      return response.data;
    } catch (err) {
      console.error('Error modifying plan:', err);
      setError(err.response?.data?.message || 'Failed to modify plan');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId]);

  /**
   * Phase 2: Build a batch of steps
   */
  const buildBatch = useCallback(async () => {
    if (!planId) {
      throw new Error('No plan ID available');
    }

    setLoading(true);
    setError(null);
    setPhase('building');

    try {
      const response = await api.post('/ai-flow-builder/multi-phase/build-batch', {
        planId,
        userApproval: true
      });

      setCheckpoint(response.data.checkpoint);

      // If no more steps, mark as completed
      if (!response.data.checkpoint.hasMore) {
        setPhase('completed');
      }

      // Return full response including flow data if complete
      return response.data;
    } catch (err) {
      console.error('Error building batch:', err);
      setError(err.response?.data?.message || 'Failed to build batch');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId]);

  /**
   * Get plan status
   */
  const getPlanStatus = useCallback(async (id) => {
    try {
      const response = await api.get(`/ai-flow-builder/multi-phase/status/${id || planId}`);
      return response.data;
    } catch (err) {
      console.error('Error getting plan status:', err);
      throw err;
    }
  }, [planId]);

  /**
   * Cancel plan
   */
  const cancelPlan = useCallback(async () => {
    if (!planId) {
      throw new Error('No plan ID available');
    }

    setLoading(true);
    setError(null);

    try {
      await api.post(`/ai-flow-builder/multi-phase/cancel/${planId}`);
      
      // Reset state
      setPlan(null);
      setPlanId(null);
      setCheckpoint(null);
      setPhase(null);
    } catch (err) {
      console.error('Error cancelling plan:', err);
      setError(err.response?.data?.message || 'Failed to cancel plan');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setPlan(null);
    setPlanId(null);
    setCheckpoint(null);
    setLoading(false);
    setError(null);
    setPhase(null);
    setAnalysisResult(null);
    setFeatureGaps([]);
  }, []);

  /**
   * NEW: Set plan from external analysis result
   * Used when the /analyze endpoint returns a plan directly
   */
  const setPlanFromAnalysis = useCallback((analysisResult) => {
    if (analysisResult && analysisResult.plan) {
      setPlan(analysisResult.plan);
      setPlanId(analysisResult.planId);
      setPhase('planned');
      console.log('📋 [Hook] Plan set from analysis:', analysisResult.planId);
    }
  }, []);

  return {
    // State
    plan,
    planId,
    checkpoint,
    loading,
    error,
    phase,
    analysisResult,
    featureGaps,

    // Actions
    analyzeRequest, // NEW: Intelligent analysis
    createPlan,
    modifyPlan,
    buildBatch,
    getPlanStatus,
    cancelPlan,
    reset,
    setPlanFromAnalysis // NEW: Set plan from external analysis
  };
};

export default useMultiPhaseFlowBuilder;

